import type { Author, PostFull, PostSummary } from './sanity';
import { ORG_ID, ORG_LOGO, pageUrl, SITE_NAME, SITE_URL } from './seo';

function blogPublisher() {
  return {
    '@type': 'Organization' as const,
    '@id': ORG_ID,
    name: SITE_NAME,
    url: SITE_URL,
    logo: ORG_LOGO,
  };
}

export function blogPostTitle(title: string) {
  return `${title} | ${SITE_NAME} Blog`;
}

function allAuthors(post: PostFull): Author[] {
  return [post.author, ...(post.coAuthors ?? [])].filter(
    (author): author is Author => author != null && Boolean(author.name),
  );
}

export function formatPostAuthorNames(post: PostFull): string {
  return allAuthors(post)
    .map((author) => author.name)
    .join(', ');
}

function buildJsonLdAuthors(post: PostFull) {
  const authors = allAuthors(post);
  if (authors.length === 0) return undefined;
  if (authors.length === 1) {
    return { '@type': 'Person', name: authors[0].name };
  }
  return authors.map((author) => ({ '@type': 'Person', name: author.name }));
}

export function buildBlogPostingJsonLd(post: PostFull, seoImage?: string) {
  const url = pageUrl(`/blog/${post.slug.current}`);
  const author = buildJsonLdAuthors(post);

  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.excerpt,
    image: seoImage,
    url,
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    datePublished: post.publishedAt,
    dateModified: post._updatedAt ?? post.publishedAt,
    ...(author ? { author } : {}),
    publisher: blogPublisher(),
  };
}

export function buildBlogIndexSchema(posts: readonly PostSummary[]) {
  const url = pageUrl('/blog');
  return {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    '@id': `${url}#blog`,
    url,
    name: `${SITE_NAME} Blog`,
    description: 'Guides, tutorials and insights on AI filmmaking from the Induce team.',
    publisher: { '@id': ORG_ID },
    blogPost: posts.map((post) => {
      const postUrl = pageUrl(`/blog/${post.slug.current}`);
      return {
        '@type': 'BlogPosting',
        '@id': postUrl,
        headline: post.title,
        url: postUrl,
        datePublished: post.publishedAt,
      };
    }),
  };
}
